import { useMsal } from '@azure/msal-react'
import { Form, Input, message } from 'antd'
import { Amplify } from 'aws-amplify'
import { confirmSignIn, getCurrentUser, signIn } from 'aws-amplify/auth'
import React, { useEffect, useState, useContext } from 'react'
import { awsExports } from '../../aws-exports'
import { loginRequest } from '../../utils/authConfig'
import { StoreContext } from '../../context/storeContext'
import CatBanner2 from '../../assets/images/CatBanner2.webp'
import KolumbusLogo from '../../assets/images/Kolumbus-Logo.png'
import AnkoPhLogo from '../../assets/images/AnkoPhLogo.png'
import styles from './Login.module.scss'

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: awsExports.USER_POOL_ID,
      userPoolClientId: awsExports.USER_POOL_APP_CLIENT_ID
    }
  }
})

const LoginPage = () => {
  const { instance } = useMsal()
  const [loading, setLoading] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false)
  const storeContext = useContext(StoreContext)
  const { setStore, clearCache } = storeContext || {}

  useEffect(() => {
    // Clear all caches and store context when login page loads
    if (clearCache) {
      clearCache()
    }

    if (setStore) {
      setStore({ name: '', id: '' }, false)
    }

    const initializeUser = async () => {
      try {
        const user = await getCurrentUser()
        if (user) {
          await storeLocalStorageAuthInfo()
          setIsAuthenticated(true)
          window.location.replace('/home')
        }
      } catch {
        setIsAuthenticated(false)
      }
    }

    // Check if already authenticated during initial mount
    initializeUser()

    // Handle redirection when authentication status changes after login
    if (isAuthenticated) {
      window.location.replace('/home')
    }
  }, [isAuthenticated, setStore, clearCache])

  const storeLocalStorageAuthInfo = async () => {
    localStorage.setItem('authenticated', 'true')
    localStorage.setItem('loginType', 'cognito')
  }

  const handleMicrosoftLogin = async () => {
    setLoading(true)
    try {
      const result = await instance.loginPopup(loginRequest)

      // Clear any existing cache before setting new authentication
      if (clearCache) {
        clearCache()
      }

      // User has valid access - store authentication info
      localStorage.setItem('authenticated', 'true')
      localStorage.setItem('loginType', 'microsoft')
      localStorage.setItem('msalAccessToken', result.accessToken)

      // Clear store context for fresh SSO login
      if (setStore) {
        setStore({ name: '', id: '' }, false)
      }

      message.success('Login successful!')
      window.location.replace('/')

    } catch (error) {
      console.error('Microsoft login error:', error)
      message.error('Microsoft login failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const onFinish = async (values) => {
    setLoading(true)
    try {
      const user = await signIn({
        username: values.username,
        password: values.password
      })

      if (
        user.isSignedIn === false &&
        user.nextStep.signInStep ===
        'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        setIsNewPasswordRequired(true)
      } else if (user.isSignedIn) {
        onLoginSuccess()
      }
    } catch (error) {
      const errorMessage =
        error.name === 'NotAuthorizedException'
          ? 'Incorrect username or password. Please try again.'
          : 'An unexpected error occurred. Please try again later.'
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordReset = async (values) => {
    if (values.newPassword !== values.confirmNewPassword) {
      message.error('New passwords do not match.')
      return
    }

    setLoading(true)
    try {
      await confirmSignIn({
        challengeResponse: values.confirmNewPassword
      })
      setIsNewPasswordRequired(false)
      onLoginSuccess()
    } catch (error) {
      message.error('Failed to reset password.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onLoginSuccess = () => {
    // Clear cache before successful login
    if (clearCache) {
      clearCache()
    }

    storeLocalStorageAuthInfo()
    setIsAuthenticated(true)
    message.success('Login successful!')
  }

  return (
    <div className={styles.loginPage}>
      <div className={styles.leftSection}>
        <div className={styles.logoContainer}>
          <img src={AnkoPhLogo} alt="Company Logo" className={styles.logo} />
          <img src={KolumbusLogo} alt="Kolumbus Logo" className={styles.kolumbusLogo} />
        </div>

        <div className={styles.formContainer}>
          <h1 className={styles.welcomeTitle}>Welcome back</h1>
          <p className={styles.subtitle}>Please enter your details</p>

          {!isNewPasswordRequired ? (
            <Form
              name="login"
              onFinish={onFinish}
              layout="vertical"
              className={styles.loginForm}
            >
              <Form.Item
                label="Username"
                name="username"
                rules={[
                  { required: true, message: 'Please enter your username!' }
                ]}
                className={styles.inputContainer}
              >
                <Input
                  placeholder="Enter your username"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: 'Please enter your password!' }
                ]}
                className={styles.inputContainer}
              >
                <Input.Password
                  placeholder="Enter your password"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>

              <Form.Item>
                <button
                  type="submit"
                  className={styles.signInButton}
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>

                <button
                  type="button"
                  onClick={handleMicrosoftLogin}
                  className={styles.microsoftButton}
                  disabled={loading}
                >
                  <img
                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAhFBMVEXz8/PzUyWBvAYFpvD/ugjz9fb19Pbz+fr39fr69vPy9foAofD/tgDzRQB9ugAAo/Df6dCv0Xjz2dPzTBfzl4PznImz04CAx/H60oHS5vJ5xPH60Hn16dIAnvDz7u3z4t7n7dzzNADzkXurz3BwtQDzvrLM36zf6/Os2PL336z07d/7z3RN8WfWAAABg0lEQVR4nO3cyVLCYBCFURwCkXlygDBFUBTf//3cSGIVf5WrDi7O9wJdp3p/Wy1JkvSrLLzqVDu8FHAzjW57JrZ34+hSH5yWg9jK187PrXx/GMZ2GF9+MZsObmKbzSvhZHgb25CQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCwUWE5i21QC/fB86Xp/dLt/DG4t/MGbf7+FNxkl9jZzTrR1TvCeXjJIWFJkv7uIbzqVDe8LAE8Lp+D+zgTu5/FS2zFKUFcrEex9ZaV8Ksf3Sol7N3FNqqFRf8+NkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQsJmhetebOtr75dmi+iO1anTKrrNJbDRsvCuDJQk6Z/1DSzvYqEfRCNJAAAAAElFTkSuQmCC"
                    alt="Microsoft Logo"
                    style={{ width: 20, height: 20, marginRight: 8 }}
                  />
                  {loading ? 'Signing in...' : 'Sign in with Microsoft'}
                </button>
              </Form.Item>
            </Form>
          ) : (
            <Form
              name="newPassword"
              onFinish={handlePasswordReset}
              layout="vertical"
              className={styles.loginForm}
            >
              <Form.Item
                label="New Password"
                name="newPassword"
                rules={[
                  {
                    required: true,
                    message: 'Please enter your new password!'
                  }
                ]}
                className={styles.inputContainer}
              >
                <Input.Password
                  placeholder="Enter your new password"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>

              <Form.Item
                label="Confirm New Password"
                name="confirmNewPassword"
                rules={[
                  {
                    required: true,
                    message: 'Please confirm your new password!'
                  }
                ]}
                className={styles.inputContainer}
              >
                <Input.Password
                  placeholder="Confirm your new password"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>

              <Form.Item>
                <button
                  type="submit"
                  className={styles.signInButton}
                  disabled={loading}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </Form.Item>
            </Form>
          )}
        </div>
      </div>
      <div className={styles.rightSection}>
        <img src={CatBanner2} alt="Banner" className={styles.backgroundImage} />
      </div>
    </div>
  )
}

export default LoginPage